import { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { Box } from '@mantine/core'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { SignInCard } from '../../SignInCard/SignInCard'
import classes from '../../SignInCard/SignInCard.module.scss'

import { Button, FormField, TextInput, Spinner, Text } from '@percent/lemonade'
import { useMutation } from '@percent/workplace-giving/common/hooks/useMutation/useMutation'
import { AppRoute } from '@percent/workplace-giving/routing/AppRoute.enum'
import { useAuth } from '@percent/workplace-giving/common/hooks'
import { verifyOtpKey } from '@percent/workplace-giving/api/auth/verifyOTPKey/verifyOTPKey'
import { useLogin } from '@percent/workplace-giving/common/hooks/useLogin/useLogin'

export function SignWithTwoFA() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { state: locationState }: { state: any } = useLocation()
  const { dispatch, state } = useAuth()
  const { login } = useLogin({ dispatch })
  const [errorMessage, setErrorMessage] = useState('')

  const redirectTo = locationState?.from?.pathname
    ? locationState.from.pathname + (locationState.from.search ?? '')
    : AppRoute.HOME

  useEffect(() => {
    if (!locationState?.loginAttempted) {
      navigate('/sign-in')
    }
  }, [locationState])

  useEffect(() => {
    if (state.status === 'authorized') {
      navigate(redirectTo, { replace: true })
    }
  }, [state.status])

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: verifyOtpKey,
    onSuccess: async ({ data: account }) => {
      try {
        await login({ account })
        navigate(redirectTo, { replace: true })
      } catch (e) {
        setErrorMessage(t('workplace_giving.errors.unexpected'))
      }
    }
  })

  const formik = useFormik({
    initialValues: {
      token: ''
    },
    validationSchema: () =>
      yup.object().shape({
        token: yup
          .string()
          .required('Required')
          .matches(/^[0-9]+$/, t('workplace_giving.errors.onlyDigits'))
          .min(6, t('workplace_giving.errors.sixDigit'))
          .max(6, t('workplace_giving.errors.sixDigit'))
      }),
    onSubmit: ({ token }: { token: string }) => {
      mutateAsync({
        token
      })
    }
  })

  const { errors, values, touched, handleChange, handleBlur, handleSubmit } = formik

  useEffect(() => {
    if (values.token) {
      setErrorMessage('')
    }
  }, [values.token, setErrorMessage])

  return (
    <SignInCard title={t('workplace_giving.2fa.title')}>
      {isLoading ? (
        <Box className={classes.CenteredFlexBox}>
          <Spinner size="l" />
        </Box>
      ) : (
        <Box component="form" className={classes.Form} onSubmit={handleSubmit}>
          <FormField
            label={t('workplace_giving.2fa.enterSixDigit')}
            status={touched.token && errors.token ? 'danger' : 'default'}
            statusMessage={errorMessage || errors.token}
            data-testid="token"
          >
            <TextInput
              name="token"
              placeholder={t('workplace_giving.2fa.placeholderSixDigit')}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.token}
              borderRadiiSize="large"
            />
          </FormField>
          <Button type="submit" data-testid="auth-active-button" shape="oval">
            {t('workplace_giving.common.confirm')}
          </Button>
          <Text>
            <span>
              <Link to="/sign-in">{t('workplace_giving.2fa.signInDifferentAccount')}</Link>
            </span>
          </Text>
        </Box>
      )}
    </SignInCard>
  )
}
